import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import {
  useGetAvailableCouponsMutation,
  useValidateCouponMutation,
  useCalculateDiscountsMutation,
} from '#services/coupon';
import { formatCurrency, formatDate } from '#common/utils';
import {
  COUPON_DISCOUNT_TYPES,
  COUPON_TARGET_TYPES,
} from '#common/constants';

type TFn = ReturnType<typeof useTranslation>['t'];

interface Coupon {
  id?: number | string | undefined;
  code?: string | undefined;
  name?: string | undefined;
  discountType?: string | undefined;
  discountValue?: number | undefined;
  targetType?: string | undefined;
  targetIds?: Array<number | string> | undefined;
  minPurchaseAmount?: number | undefined;
  endDate?: string | number | Date | undefined;
  [key: string]: unknown;
}

interface CartItem {
  product?: { id?: number | string; [key: string]: unknown };
  productId?: number | string;
  unitPrice?: number;
  amounts?: number;
  [key: string]: unknown;
}

interface DiscountLine {
  couponCode?: string;
  couponId?: number | string;
  description?: string;
  discount?: number;
  [key: string]: unknown;
}

interface DiscountInfo {
  appliedCoupons?: DiscountLine[];
  invalidCoupons?: unknown[];
  totalDiscount?: number;
  subtotal?: number;
  total?: number;
  [key: string]: unknown;
}

interface ValidationData {
  valid?: boolean;
  message?: string;
  coupon?: Coupon;
  error?: unknown;
  [key: string]: unknown;
}

interface CouponCheckoutProps {
  cartItems?: CartItem[];
  customerId?: number;
  appliedCoupons?: Coupon[];
  onAppliedCouponsChange?: (coupons: Coupon[]) => void;
  discountInfo?: DiscountInfo;
  onDiscountInfoChange?: (info: DiscountInfo) => void;
}

function getDiscountDescription(coupon: Coupon, t: TFn) {
  const typeDesc = coupon.discountType === COUPON_DISCOUNT_TYPES.PERCENT
    ? t('discount.percentOff', { value: coupon.discountValue })
    : t('discount.amountOff', { amount: formatCurrency(coupon.discountValue as number) });

  let targetDesc;
  if (coupon.targetType === COUPON_TARGET_TYPES.CART) {
    targetDesc = t('discount.entireCart');
  } else if (coupon.targetType === COUPON_TARGET_TYPES.COLLECTION) {
    targetDesc = t('discount.selectCollections');
  } else {
    targetDesc = t('discount.selectProducts');
  }

  return t('discount.description', { type: typeDesc, target: targetDesc });
}

/**
 * CouponCheckout - Component for applying coupons during checkout
 */
export default function CouponCheckout({
  cartItems = [],
  customerId,
  appliedCoupons = [],
  onAppliedCouponsChange,
  discountInfo,
  onDiscountInfoChange,
}: CouponCheckoutProps) {
  const { t } = useTranslation('coupon');
  const [expanded, setExpanded] = useState(true);
  const [availableCoupons, setAvailableCoupons] = useState<Coupon[]>([]);
  const [manualCode, setManualCode] = useState('');
  const [validationError, setValidationError] = useState('');

  const [getAvailableCoupons] = useGetAvailableCouponsMutation();
  const [validateCoupon] = useValidateCouponMutation();
  const [calculateDiscounts] = useCalculateDiscountsMutation();

  // Prepare cart items with unitPrice for validation
  const preparedCartItems = cartItems.map((item) => ({
    ...item,
    productId: item.product?.id || item.productId,
    unitPrice: item.unitPrice,
    amounts: item.amounts,
  }));

  // Fetch available coupons
  useEffect(() => {
    // Don't fetch if we don't have a customerId
    if (!customerId) return;

    getAvailableCoupons({ customerId })
      .then((res) => {
        const resData = ('data' in res ? res.data : undefined) as { error?: unknown; data?: Coupon[] } | undefined;
        if (resData && !resData.error) {
          // API returns coupons directly in res.data (not res.data.data)
          const coupons = resData.data || resData;
          setAvailableCoupons(Array.isArray(coupons) ? coupons : []);
        }
      })
      .catch((err) => {
        console.error('Error fetching available coupons:', err);
        setAvailableCoupons([]);
      });
  }, [customerId]);

  // Recalculate discounts when applied coupons or cart items change
  useEffect(() => {
    if (appliedCoupons.length === 0) {
      const subtotal = preparedCartItems.reduce((sum, item) => sum + (item.unitPrice || 0) * (item.amounts || 0), 0);
      onDiscountInfoChange?.({
        appliedCoupons: [],
        invalidCoupons: [],
        totalDiscount: 0,
        subtotal,
        total: subtotal,
      });
      return;
    }

    void calculateDiscounts({
      couponCodes: appliedCoupons.map((c) => c.code),
      cartItems: preparedCartItems,
      customerId,
    }).then((res) => {
      const resData = ('data' in res ? res.data : undefined) as { error?: unknown; data?: DiscountInfo } & DiscountInfo | undefined;
      if (resData && !resData.error) {
        // Handle both possible response structures
        const discountData = (resData.data || resData);
        onDiscountInfoChange?.(discountData);
      }
    });
  }, [appliedCoupons.length, cartItems.reduce((s, i) => s + (i.amounts || 0), 0), customerId]);

  const handleApplyCoupon = async (coupon: Coupon) => {
    // Check if already applied
    if (appliedCoupons.some((c) => c.id === coupon.id)) {
      return;
    }

    // Validate coupon
    const res = await validateCoupon({
      couponCode: coupon.code,
      cartItems: preparedCartItems,
      customerId,
    });

    const resData = ('data' in res ? res.data : undefined) as { error?: unknown; data?: ValidationData } & ValidationData | undefined;
    if (resData && !resData.error) {
      // Handle both possible response structures
      const validationData = (resData.data || resData);

      if (validationData.valid) {
        const newApplied = [...appliedCoupons, {
          id: coupon.id,
          code: coupon.code,
          name: coupon.name,
          discountType: coupon.discountType,
          discountValue: coupon.discountValue,
          targetType: coupon.targetType,
          targetIds: coupon.targetIds,
          minPurchaseAmount: coupon.minPurchaseAmount,
        }];
        onAppliedCouponsChange?.(newApplied);
        setValidationError('');
      } else {
        setValidationError(validationData.message || '');
      }
    }
  };

  const handleApplyManualCode = async () => {
    if (!manualCode.trim()) return;

    // Check if already applied
    if (appliedCoupons.some((c) => c.code?.toLowerCase() === manualCode.toLowerCase())) {
      setValidationError(t('alreadyApplied'));
      return;
    }

    const res = await validateCoupon({
      couponCode: manualCode.trim(),
      cartItems: preparedCartItems,
      customerId,
    });

    const resData = ('data' in res ? res.data : undefined) as { error?: unknown; data?: ValidationData } & ValidationData | undefined;
    if (resData && !resData.error) {
      // Handle both possible response structures
      const validationData = (resData.data || resData);

      if (validationData.valid) {
        const coupon = validationData.coupon || {};
        const newApplied = [...appliedCoupons, {
          id: coupon.id,
          code: coupon.code,
          name: coupon.name,
          discountType: coupon.discountType,
          discountValue: coupon.discountValue,
          targetType: coupon.targetType,
          targetIds: coupon.targetIds,
          minPurchaseAmount: coupon.minPurchaseAmount,
        }];
        onAppliedCouponsChange?.(newApplied);
        setManualCode('');
        setValidationError('');
      } else {
        setValidationError(validationData.message || '');
      }
    }
  };

  const handleRemoveCoupon = (couponId?: number | string) => {
    const newApplied = appliedCoupons.filter((c) => c.id !== couponId);
    onAppliedCouponsChange?.(newApplied);
  };

  const isApplied = (couponId?: number | string) => appliedCoupons.some((c) => c.id === couponId);

  return (
    <Paper variant="outlined" sx={{ mt: 3, mb: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          bgcolor: 'grey.50',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOfferIcon color="primary" />
          <Typography variant="subtitle1" sx={{
            fontWeight: "bold"
          }}>
            {t('title')}
          </Typography>
          {appliedCoupons.length > 0 && (
            <Chip
              size="small"
              label={t('appliedCount', { count: appliedCoupons.length })}
              color="success"
            />
          )}
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {/* Manual code entry */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder={t('enterCode')}
              value={manualCode}
              onChange={(e) => {
                setManualCode(e.target.value);
                setValidationError('');
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  void handleApplyManualCode();
                }
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={() => { void handleApplyManualCode(); }}
              disabled={!manualCode.trim()}
            >
              {t('apply')}
            </Button>
          </Box>

          {/* Validation error */}
          {validationError && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setValidationError('')}>
              {validationError}
            </Alert>
          )}

          {/* Available coupons */}
          {availableCoupons.length > 0 && (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 1
                }}>
                {t('availableCoupons')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {availableCoupons.map((coupon) => (
                  <Paper
                    key={coupon.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: isApplied(coupon.id) ? 'success.50' : 'transparent',
                      borderColor: isApplied(coupon.id) ? 'success.main' : 'divider',
                    }}
                  >
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            fontFamily: 'monospace'
                          }}>
                          {coupon.code}
                        </Typography>
                        {isApplied(coupon.id) && (
                          <CheckCircleIcon fontSize="small" color="success" />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{
                        color: "text.secondary"
                      }}>
                        {getDiscountDescription(coupon, t)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "text.secondary"
                        }}>
                        {t('expires', { date: formatDate(coupon.endDate as string | number | Date) })}
                        {(coupon.minPurchaseAmount || 0) > 0 && (
                          <>
                            {' '}• {t('minLabel', { amount: formatCurrency(coupon.minPurchaseAmount as number) })}
                          </>
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      {isApplied(coupon.id) ? (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRemoveCoupon(coupon.id)}
                        >
                          {t('remove')}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => { void handleApplyCoupon(coupon); }}
                        >
                          {t('apply')}
                        </Button>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </>
          )}

          {/* Applied coupons summary */}
          {appliedCoupons.length > 0 && discountInfo && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  mb: 1
                }}>
                {t('appliedDiscounts')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {discountInfo.appliedCoupons?.map((ac) => (
                  <Box
                    key={ac.couponCode}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveCoupon(ac.couponId)}
                        sx={{ p: 0.25 }}
                      >
                        <CancelIcon fontSize="small" color="error" />
                      </IconButton>
                      <Typography variant="body2">
                        <span style={{ fontFamily: 'monospace' }}>{ac.couponCode}</span>
                        {' - '}
                        {ac.description}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "success.main",
                        fontWeight: "bold"
                      }}>
                      -{formatCurrency(ac.discount as number)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Total discount */}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" sx={{
                  fontWeight: "bold"
                }}>
                  {t('totalDiscount')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "success.main",
                    fontWeight: "bold"
                  }}>
                  -{formatCurrency(discountInfo.totalDiscount as number)}
                </Typography>
              </Box>
            </>
          )}

          {/* Empty state */}
          {availableCoupons.length === 0 && appliedCoupons.length === 0 && (
            <Typography
              variant="body2"
              align="center"
              sx={{
                color: "text.secondary",
                py: 2
              }}>
              {t('emptyState')}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

export { getDiscountDescription };
